import {Button, Grid, Typography} from '@mui/material';
import React, {useEffect} from 'react';
import {useMutation} from '@apollo/client';
import {Box} from '@mui/system';
import {useSnackbar} from 'notistack';
import moment from 'moment';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {
  ReportPackGenericFormType,
  reportPackFormType,
  ReportPackGenericFormInnerType,
  reportPackTaxYearField,
  reportPackJisaFormSubscriptionTypeField,
  reportPackIsaAmountField,
} from './ReportPackFields';
import {upsertJisaApplicationForm} from './reportPackQueries';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../shared/UPDATES';
import {
  UpsertJisaApplicationForm,
  UpsertJisaApplicationFormVariables,
} from '../../schema-types/UpsertJisaApplicationForm';
import {getNumberOrNull} from '../../utils/getNumberOrNull';
import {getTaxyear, getTaxYear4Digits2Digits} from '../../utils/getTaxYear';
import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';
import {getReportPackMissingFieldError} from './getReportPackMissingFieldError';
import {DependantForm} from './DependantForm';

export const ReportPackJISAApplication: React.FC<ReportPackGenericFormType> = ({
  formData,
  onClose,
  disabled,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [onUpsertJISAForm, {loading: onUpsertJISAFormLoading}] = useMutation<
    UpsertJisaApplicationForm,
    UpsertJisaApplicationFormVariables
  >(upsertJisaApplicationForm);
  return (
    <ReportPackJISAApplicationFormWrapped
      formFields={[]}
      disabled={disabled}
      submit={async (values) => {
        if (disabled) {
          return;
        }
        closeSnackbar();
        enqueueSnackbar(`Saving ${formData.formTypeLabel}...`, {
          variant: 'info',
        });
        await onUpsertJISAForm({
          variables: {
            contactCuroId: values.contactCuroId,
            reportPackId: String(formData.reportPackId),
            formId: formData.id,
            jisaFormSubscriptionType: values.jisaFormSubscriptionType,
            taxYear: getTaxYear4Digits2Digits(values.taxYear),
            isaAmount: getNumberOrNull(values.isaAmount),
            childDob: moment(values.childDob),
            childForenames: values.childForenames,
            childNiNumber: values.childNiNumber,
            childPostcode: values.childPostcode,
            childSurname: values.childSurname,
            childAddress: values.childAddress,
            childTitle: values.childTitle,
          },
        })
          .then(({data}) => {
            if (data?.upsertJisaApplicationForm?.ok) {
              closeSnackbar();
              enqueueSnackbar(`Successfully saved ${formData.formTypeLabel}`, {
                variant: 'success',
              });
              onClose();
            } else {
              throw Error(data?.upsertJisaApplicationForm?.errors?.join(','));
            }
          })
          .catch((err) => {
            closeSnackbar();
            enqueueSnackbar(`Could not save ${formData.formTypeLabel} ${err}`, {
              variant: 'error',
            });
          });
      }}
      initialValueOverrides={{
        ...formData,
        reportPackId: String(formData.reportPackId),
        contactCuroId: String(formData.contactCuroId),
      }}
    />
  );
};

const ReportPackJISAApplicationForm: React.FC<ReportPackGenericFormInnerType> = ({
  disabled,
}) => {
  const {submitForm, isSubmitting, values, setValues} =
    useAutoForm<ReportPackFormFragment>();

  useEffect(() => {
    if (values.taxYear) {
      return;
    }

    const taxYear = getTaxyear(new Date());
    const formattedTaxYear = `${taxYear.endYear}/${taxYear.startYear
      ?.toString()
      .slice(-2)}`;

    setValues(UPDATES._2201, (previous) => {
      return {
        ...previous,
        taxYear: formattedTaxYear,
      };
    });
  }, [values.taxYear]);

  return (
    <Box maxWidth={500}>
      <Typography>JISA Aplication details:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackTaxYearField.id)}
            field={reportPackTaxYearField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackJisaFormSubscriptionTypeField.id
            )}
            field={reportPackJisaFormSubscriptionTypeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackIsaAmountField.id)}
            field={reportPackIsaAmountField}
          />
        </Grid>
      </Grid>
      <DependantForm disabled={disabled} />
      <Box position="absolute" bottom={20} right={20}>
        {!disabled && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isSubmitting}
            onClick={() => submitForm()}
          >
            Save form
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ReportPackJISAApplicationFormWrapped = withAutoFormNew<
  reportPackFormType,
  ReportPackGenericFormInnerType
>(ReportPackJISAApplicationForm, {}, {debugPosition: 'bottom'});
